import { useContext, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import Paginator from '../../../components/Paginator';
import FileItem from './FileItem';
import { FileInterface, FileContext } from '../../../contexts/FileContext';
import ModalComponent from '../../../components/ModalComponent';

interface ModalBodyProps {
  modalProperties: { type: 'delete' | 'create' | 'edit' | null }
  file: FileInterface
  inputFile: File | null
  setInputFile: (file: File) => void
  errors: any
  fileNameError: string;
  setFileNameError: (msg: string) => void;
  editableFileName: string;
  setEditableFileName: React.Dispatch<React.SetStateAction<string>>;
  fileExtension: string;
  setFileExtension: React.Dispatch<React.SetStateAction<string>>;
}

const validateFileName = (name: string): string => {
  const allowedPattern = /^[\w-]*$/;
  if (!allowedPattern.test(name)) {
    return 'ファイル名に使用できない文字が含まれています。';
  } if (name.trim() === '') {
    return 'ファイル名を入力してください。';
  }
  return '';
};

const ModalBody = ({
  modalProperties,
  file,
  inputFile,
  setInputFile,
  errors,
  fileNameError,
  setFileNameError,
  editableFileName,
  setEditableFileName,
  fileExtension,
  setFileExtension,
}: ModalBodyProps) => {
  let fileName = '';
  if (inputFile) {
    fileName = inputFile.name;
  } else if (modalProperties.type === 'create') {
    fileName = '';
  } else {
    fileName = file ? file.file_name : '';
  }

  useEffect(() => {
    let baseName = '';
    if (inputFile) {
      baseName = inputFile.name;
    } else if (modalProperties.type === 'create') {
      baseName = '';
    } else {
      baseName = file ? file.file_name : '';
    }

    const getFileBaseName = (name: string): string => {
      const parts = name.split('.');
      if (parts.length <= 1) return name;
      parts.pop();
      return parts.join('.');
    };

    const getFileExtension = (name: string) => {
      const parts = name.split('.');
      return parts.length > 1 ? `.${parts.pop()}` : '';
    };
    const sanitize = (name: string) => name
      .normalize('NFKC')
      .replace(/[\u4E00-\u9FFF]/g, '') // Remove Kanji
      .replace(/[^\w-]/g, ''); // Keep only a-zA-Z0-9_-
    const rawBaseName = getFileBaseName(baseName);
    // Only sanitize when a new file is selected, not when editing existing files
    if (inputFile) {
      setEditableFileName(sanitize(rawBaseName));
      setFileNameError(''); // Clear error for new files
    } else {
      setEditableFileName(rawBaseName);
      // Validate existing file names
      if (rawBaseName) {
        setFileNameError(validateFileName(rawBaseName));
      } else {
        setFileNameError('');
      }
    }
    setFileExtension(getFileExtension(baseName));
  }, [inputFile, file, modalProperties.type, setEditableFileName, setFileExtension, setFileNameError]);

  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setEditableFileName(newName);
    setFileNameError(validateFileName(newName));
  };

  if (modalProperties.type === 'delete') {
    return (
      <span>
        {`「${fileName}」を削除してもよろしいですか？`}
      </span>
    );
  }

  return (
    <>
      {errors && Object.keys(errors).length > 0 && (
        <div className="p-1 alert alert-danger" role="alert">
          {(() => {
            const firstErrorKey = Object.keys(errors)[0];
            const firstError = errors[firstErrorKey];
            return Array.isArray(firstError) ? firstError[0] : firstError;
          })()}
        </div>
      )}

      <form className="d-flex flex-column h-100">
        <div className="row">
          <div className="col-12">
            <div className="row g-3 mb-3">
              <label className="col-form-label col-sm-2 text-sm-start">
                ファイル
                <span>*</span>
              </label>
              <div className="col-sm-10">
                <div className="d-flex justify-content-center align-items-center">
                  <input
                    type="text"
                    className="form-control me-2"
                    readOnly
                    placeholder="ファイルを選択してください"
                    id="fileselect"
                    value={fileName}
                  />
                  <label className="input-group-btn">
                    <span className="btn btn-primary text-nowrap">
                      ファイル選択
                      <input
                        type="file"
                        style={{ display: 'none' }}
                        ref={(input) => {
                          if (input) {
                            input.addEventListener('change', () => {
                              if (input.files) {
                                setInputFile(input.files[0]);
                                document.getElementById('fileselect')?.setAttribute('value', input.files[0].name);
                              }
                            });
                          }
                        }}
                      />
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <div className="row g-3 mb-3">
              <label className="col-form-label col-sm-2 text-sm-start">
                ファイル名
                <span>*</span>
              </label>
              <div className="col-sm-10">
                <div className="input-group">
                  <input
                    name="fileNameInput"
                    type="text"
                    className="form-control"
                    autoComplete="off"
                    readOnly={modalProperties.type === 'edit' ? false : !inputFile}
                    placeholder="ファイル名を入力してください"
                    onChange={handleFileNameChange}
                    value={editableFileName}
                  />
                  <span className="input-group-text text-bg-primary" style={{ width: '115px', backgroundColor: '#2664B1', borderColor: '#2664B1' }}>
                    {fileExtension}
                  </span>
                </div>
                <div className="m-1 text-muted">
                  登録するファイル名を入力してください。（英数字と記号は「 - 」と「 _ 」のみです）
                </div>
                {fileNameError && (
                  <div className="invalid-feedback d-block">
                    {fileNameError}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

function FilesPage(): React.ReactElement {
  const {
    total,
    files,
    currentPage,
    paginationPageInfo,
    errors,
    links,
    create,
    edit,
    deleteFile,
    fetch,
    rename,
    isLoading,
    clearErrors,
  } = useContext(FileContext);

  const [fileNameError, setFileNameError] = useState('');
  const [localErrors, setLocalErrors] = useState(errors);
  const [inputFile, setInputFile] = useState<File | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [modalProperties, setModalProperties] = useState<{ isOpen: boolean, type: 'delete' | 'create' | 'edit' | null }>({ isOpen: false, type: null });
  const [editableFileName, setEditableFileName] = useState('');
  const [fileExtension, setFileExtension] = useState('');

  const openEditModal = (index: number) => {
    setSelectedIndex(index);
    setModalProperties({ isOpen: true, type: 'edit' });
    if (clearErrors) clearErrors();

    const f = files[index];
    if (!f) return;

    const parts = f.file_name.split('.');
    const ext = parts.length > 1 ? `.${parts.pop()}` : '';
    const base = parts.join('.');
    setEditableFileName(base);
    setFileExtension(ext);
  };

  const handleModalClose = () => {
    setModalProperties({ ...modalProperties, isOpen: false });
    setInputFile(null);
    setLocalErrors({});
    setSelectedIndex(null);
    setFileNameError('');
    if (clearErrors) clearErrors();
  };

  const handleConfirm = () => {
    if (modalProperties.type === 'delete') {
      if (selectedIndex === null) return;
      deleteFile(
        files[selectedIndex].id,
        handleModalClose,
      );
    } else if (modalProperties.type === 'edit') {
      if (selectedIndex === null) {
        handleModalClose();
        return;
      }

      if (inputFile) {
        const renamedFile = new File(
          [inputFile],
          editableFileName + fileExtension,
          { type: inputFile.type },
        );

        edit(
          files[selectedIndex].id,
          renamedFile,
          handleModalClose,
          () => {
            setModalProperties({ ...modalProperties, isOpen: true });
          },
        );
      } else {
        rename(
          files[selectedIndex].id,
          editableFileName,
          handleModalClose,
        );
      }
    } else {
      if (inputFile === null) {
        handleModalClose();
        return;
      }

      const renamedFile = new File(
        [inputFile],
        editableFileName + fileExtension,
        { type: inputFile.type },
      );

      create(
        renamedFile,
        handleModalClose,
        () => {
          setModalProperties({ ...modalProperties, isOpen: true });
        },
      );
    }
  };

  useEffect(() => {
    fetch(1);
  }, [fetch]);

  useEffect(() => {
    setLocalErrors(errors);
  }, [errors]);

  return (
    <>
      <main style={{
        overflowY: 'auto',
        height: 'calc(100vh - 65px)',
      }}
      >
        <div className="container-fluid p-3">
          <div className="d-flex align-items-center mb-3">
            <h3 className="h3 d-inline align-middle">ファイル管理</h3>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className="d-flex align-items-center gap-1">
                    <div className="flex-fill">
                      <h5 className="card-title">ファイル一覧</h5>
                    </div>
                    <Button
                      type="button"
                      className="btn btn-primary"
                      data-bs-toggle="modal"
                      data-bs-target="#itemeditModal"
                      onClick={() => {
                        setModalProperties({ isOpen: true, type: 'create' });
                        if (clearErrors) clearErrors();
                      }}
                    >
                      新規作成
                    </Button>
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive border-bottom">
                    <table className="table table-striped table-hover table-bordered table-nowrap">
                      <thead>
                        <tr>
                          <th className="text-center" style={{ width: '5%' }}>No</th>
                          <th>ファイル名</th>
                          <th className="text-center" style={{ width: '100px' }}>操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {files.map((file, index) => (
                          <FileItem
                            index={index + 1}
                            key={file.id}
                            fileName={file.file_name}
                            setSelectedIndex={setSelectedIndex}
                            openEditModal={() => openEditModal(index)}
                            openDeleteModal={() => setModalProperties({ isOpen: true, type: 'delete' })}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="card-footer bg-white pt-1 pb-3">
                  <div className="d-flex mt-2">
                    <div className="page-item-count">
                      <div style={{ fontSize: 14 }}>
                        {`全 ${total} 件中 ${paginationPageInfo.from ?? 0} 〜 ${paginationPageInfo.to ?? 0} 件を表示`}
                      </div>
                    </div>
                    <Paginator
                      currentPage={currentPage}
                      changePage={(page) => fetch(page)}
                      links={links}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <ModalComponent
        style={{ zIndex: 1050 }}
        show={modalProperties.isOpen}
        centered
        size={modalProperties.type === 'delete' ? undefined : 'lg'}
        onConfirm={handleConfirm}
        closeModal={handleModalClose}
        title={modalProperties.type === 'delete' ? 'ファイル削除' : 'ファイル登録'}
        confirmButtonColor={modalProperties.type === 'delete' ? 'danger' : 'primary'}
        confirmButtonText={modalProperties.type === 'delete' ? '削除する' : '保存する'}
        cancelButtonColor="outline-secondary"
        cancelButtonText={modalProperties.type === 'delete' ? 'キャンセル' : '閉じる'}
        loading={isLoading}
        disabled={!!fileNameError}
      >
        <ModalBody
          file={files[selectedIndex ?? 0]}
          inputFile={inputFile}
          setInputFile={setInputFile}
          modalProperties={modalProperties}
          errors={localErrors}
          fileNameError={fileNameError}
          setFileNameError={setFileNameError}
          editableFileName={editableFileName}
          setEditableFileName={setEditableFileName}
          fileExtension={fileExtension}
          setFileExtension={setFileExtension}
        />
      </ModalComponent>
    </>
  );
}

export default FilesPage;
